import {
    Alert,
    Button,
    Checkbox,
    FormControlLabel,
    Stack,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import FlussNodePanelField from "./FlussNodePanelField";
import FlussNodePanelShell from "./FlussNodePanelShell";
import { NodeNameField } from "./ActivityNodePanels";
import { primaryBindingFor, signalEventTypeFor } from "./fluss-node-panel-utils";

interface SubprocessPanelProps {
    selection: FlussElementSelection;
    modeler: any;
    canViewAdvanced: boolean;
    readOnly: boolean;
    onChanged: () => void;
}

const EVENT_DEFINITION_LABEL_KEYS: Record<string, { key: string; defaultValue: string }> = {
    "bpmn:TimerEventDefinition": { key: "fluss.node_panel.event_subprocess.trigger.timer", defaultValue: "Timer" },
    "bpmn:SignalEventDefinition": { key: "fluss.node_panel.event_subprocess.trigger.signal", defaultValue: "Signal" },
    "bpmn:ConditionalEventDefinition": { key: "fluss.node_panel.event_subprocess.trigger.conditional", defaultValue: "Condition" },
    "bpmn:ErrorEventDefinition": { key: "fluss.node_panel.event_subprocess.trigger.error", defaultValue: "Error" },
    "bpmn:EscalationEventDefinition": { key: "fluss.node_panel.event_subprocess.trigger.escalation", defaultValue: "Escalation" },
};

// The event sub-process's typed internal start is a plain sibling shape drawn on the canvas
// (bpmn-js already renders/authors it generically, no Fluss customization needed there) — this
// panel finds the typed Start so it can summarize its binding and select it for editing.
const internalEventStart = (element: any) =>
    (element?.children || []).find((child: any) =>
        child.type === "bpmn:StartEvent" && (child.businessObject?.eventDefinitions?.length || 0) > 0);

const conditionSummary = (raw: unknown) => {
    if (!raw) return "";
    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        const first = parsed?.type === "logical" ? parsed.conditions?.[0] : parsed;
        return [first?.field, first?.operator, first?.value].filter((part) => part !== undefined && part !== "").join(" ");
    } catch (_error) {
        return String(raw);
    }
};

const triggerSummary = (start: any, definitionType: string | undefined, t: (key: string, options: any) => string) => {
    const binding = primaryBindingFor(start.businessObject);
    if (definitionType === "bpmn:TimerEventDefinition") {
        const value = binding?.durationMinutes
            ? `${binding.durationMinutes} min`
            : binding?.date || binding?.timeOfDay || binding?.cron || "";
        return value ? t("fluss.node_panel.event_subprocess.summary.timer", { defaultValue: "Timer: {{value}}", value }) : "";
    }
    if (definitionType === "bpmn:SignalEventDefinition") {
        const value = signalEventTypeFor(start.businessObject);
        return value ? t("fluss.node_panel.event_subprocess.summary.signal", { defaultValue: "Event: {{value}}", value }) : "";
    }
    if (definitionType === "bpmn:ConditionalEventDefinition") {
        const value = conditionSummary(binding?.condition);
        return value ? t("fluss.node_panel.event_subprocess.summary.conditional", { defaultValue: "When: {{value}}", value }) : "";
    }
    if (definitionType === "bpmn:ErrorEventDefinition") {
        const codes = String(binding?.errorCodes || "any");
        const value = codes === "any"
            ? t("fluss.node_panel.event_subprocess.summary.error_any", { defaultValue: "any" })
            : codes;
        return t("fluss.node_panel.event_subprocess.summary.error", { defaultValue: "Errors: {{value}}", value });
    }
    return "";
};

export const SubprocessNodePanel = ({ selection, modeler, canViewAdvanced, readOnly, onChanged }: SubprocessPanelProps) => {
    const { t } = useTranslation();
    return (
        <FlussNodePanelShell
            selection={selection}
            kind="subprocess"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "info",
                label: t("fluss.node_panel.tab.info", { defaultValue: "Info" }),
                content: <NodeNameField selection={selection} modeler={modeler} readOnly={readOnly} onChanged={onChanged} />,
            }]}
        />
    );
};

export const EventSubprocessNodePanel = ({ selection, modeler, canViewAdvanced, readOnly, onChanged }: SubprocessPanelProps) => {
    const { t } = useTranslation();
    const internalStart = internalEventStart(selection.element);
    const definitionType = internalStart?.businessObject?.eventDefinitions?.[0]?.$type;
    const triggerLabel = definitionType && EVENT_DEFINITION_LABEL_KEYS[definitionType];
    const summary = internalStart && triggerSummary(internalStart, definitionType, t);
    const [interrupting, setInterrupting] = useState(internalStart?.businessObject?.isInterrupting !== false);

    useEffect(() => {
        setInterrupting(internalEventStart(selection.element)?.businessObject?.isInterrupting !== false);
    }, [selection.id, selection.businessObject, selection.element]);

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="event_subprocess"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "info",
                label: t("fluss.node_panel.tab.info", { defaultValue: "Info" }),
                content: (
                    <Stack spacing={1.25}>
                        <FlussNodePanelField label={t("fluss.node_panel.event_subprocess.trigger", { defaultValue: "Trigger" })}>
                            {triggerLabel ? (
                                <Typography variant="body2">
                                    {t(triggerLabel.key, { defaultValue: triggerLabel.defaultValue })}
                                </Typography>
                            ) : (
                                <Alert severity="info">
                                    {t("fluss.node_panel.event_subprocess.no_trigger", {
                                        defaultValue: "Add a typed start event (timer, signal, condition or error) inside this block to configure its trigger.",
                                    })}
                                </Alert>
                            )}
                        </FlussNodePanelField>
                        {internalStart && (
                            <>
                                {summary && <Typography variant="body2" color="text.secondary" noWrap title={summary}>{summary}</Typography>}
                                <Button
                                    size="small"
                                    sx={{ alignSelf: "flex-start" }}
                                    onClick={() => modeler?.get("selection")?.select(internalStart)}
                                >
                                    {t("fluss.node_panel.event_subprocess.configure_trigger", { defaultValue: "Configure trigger" })}
                                </Button>
                            </>
                        )}
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={interrupting}
                                    disabled={readOnly || !internalStart}
                                    onChange={(event) => {
                                        setInterrupting(event.target.checked);
                                        modeler?.get("modeling")?.updateProperties(internalStart, { isInterrupting: event.target.checked });
                                        onChanged();
                                    }}
                                />
                            )}
                            label={t("fluss.node_panel.event_subprocess.interrupting", {
                                defaultValue: "Interrupt the enclosing process/block when it triggers",
                            })}
                        />
                    </Stack>
                ),
            }]}
        />
    );
};
